import React, { useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Paper from '@material-ui/core/Paper';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import Popper from '@material-ui/core/Popper';
import { emphasize } from '@material-ui/core/styles/colorManipulator';
import Icon from 'components/Utils/NewIcon';
import { Controller } from 'react-hook-form';

const styles = (theme) => ({
  chip: {
    margin: `${theme.spacing.unit / 2}px ${theme.spacing.unit / 4}px`,
  },
  chipFocused: {
    backgroundColor: emphasize(
      theme.palette.type === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
      0.08,
    ),
  },
  noOptionsMessage: {
    padding: `${theme.spacing.unit}px ${theme.spacing.unit * 2}px`,
  },
  singleValue: {
    fontSize: 16,
  },
  placeholder: {
    position: 'absolute',
    left: 2,
    fontSize: 16,
  },
  paper: {
    position: 'absolute',
    zIndex: 1,
    marginTop: theme.spacing.unit,
    left: 0,
    right: 0,
    width: 'max-content',
  },
  divider: {
    height: theme.spacing.unit * 2,
  },
  input: {
    display: 'flex',
    marginTop: 4,
  },
  valueContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
  },
  root: {
    flexGrow: 1,
    height: 250,
    width: 200,
  },
});

function NoOptionsMessage(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.noOptionsMessage}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

function inputComponent({ inputRef, ...props }) {
  return <div ref={inputRef} {...props} />;
}

function Control(props) {
  return (
    <TextField
      fullWidth
      classes={{ root: 'truncate' }}
      InputProps={{
        inputComponent,
        inputProps: {
          className: props.selectProps.classes.input + ' default-auto-complete',
          inputRef: props.innerRef,
          children: props.children,
          ...props.innerProps,
        },
      }}
      {...props.selectProps.textFieldProps}
      onChange={props.selectProps.onUpdateInput}
    />
  );
}

function Option(props) {
  return (
    <MenuItem
      buttonRef={props.innerRef}
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400,
      }}
      {...props.innerProps}
      className={`${props.innerProps.className || ''} ${
        props.isFocused ? 'menu-item-focused' : ''
      }`}
    >
      {props.children}
    </MenuItem>
  );
}

function Placeholder(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.placeholder}
      {...props.innerProps}
    >
      {props.selectProps.placeholder || props.children}
    </Typography>
  );
}

function SingleValue(props) {
  return (
    <Typography className={props.selectProps.classes.singleValue} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

function ValueContainer(props) {
  return <div className={props.selectProps.classes.valueContainer}>{props.children}</div>;
}

function Menu(props) {
  return (
    <Paper square className={props.selectProps.classes.paper} {...props.innerProps}>
      {props.children}
    </Paper>
  );
}

/**
 * @param {any} props
 */
function DefaultAutoComplete(props) {
  const anchorEl = useRef(null);
  const selectRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const MenuWithPopper = useCallback((menuProps) => {
    let ref = anchorEl?.current;
    return (
      !!ref && (
        <Popper
          anchorEl={ref}
          open={!!ref}
          style={{
            zIndex: '10000',
            marginLeft: '-290px',
          }}
          placement={props.placement}
        >
          {Menu(menuProps)}
        </Popper>
      )
    );
  }, []);

  const handleMenuOpen = useCallback(() => {
    setIsMenuOpen(true);

    const { clearable, input, onChange } = props.field || props;

    if (clearable) {
      if (input) input.onChange('');
      else if (onChange) onChange('');
    }

    if (props.onMenuOpen) props.onMenuOpen();
  }, [props]);

  const handleMenuClose = useCallback(() => {
    setIsMenuOpen(false);
    if (props.onMenuClose) props.onMenuClose();
  }, [props.onMenuClose]);

  const handleKeyDown = useCallback(
    (event) => {
      const { onKeyDown } = props.field || props;

      const hasFocusedOption = document.querySelector('.menu-item-focused');

      const isSelection = event.key === 'Enter' && isMenuOpen && !!hasFocusedOption;

      if (onKeyDown) {
        onKeyDown(event, isSelection);
      }
    },
    [props, isMenuOpen],
  );

  // RHF mode: when control + name are provided, wrap in Controller
  if (props.control && props.name) {
    const {
      control,
      name,
      onUpdateInput: outerOnUpdateInput,
      onOptionSelected: outerOnOptionSelected,
      ...rest
    } = props;
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const WrappedAutoComplete = DefaultAutoCompleteStyled;
          return (
            <WrappedAutoComplete
              //@ts-ignore
              input={{
                value: field.value ?? '',
                onChange: (value) => field.onChange(value),
              }}
              id={props.id ?? `select-${name}`}
              onUpdateInput={(value) => {
                field.onChange(value);
                outerOnUpdateInput?.(value);
              }}
              onOptionSelected={outerOnOptionSelected}
              {...rest}
            />
          );
        }}
      />
    );
  }

  const {
    dataSource,
    placeholder,
    options,
    label,
    style,
    input,
    id,
    onUpdateInput,
    onChange,
    onOptionSelected,
    allowClear,
    inputProps,
    autoFocus = undefined,
    clearable = false,
    popOut = false,
    loading = false,
    maxMenuHeight,
  } = props.field || props;
  const { classes, theme } = props;

  let value = input?.value || '';

  const selectStyles = {
    input: (base) => ({
      ...base,
      color: theme.palette.text.primary,
      '& input': {
        font: 'inherit',
      },
    }),
  };

  const finalOptions = (() => {
    if (options) {
      return options;
    }
    return (dataSource || []).map((it) => {
      if (typeof it === 'object') {
        return it;
      }
      return { value: it, label: it };
    });
  })();

  value = value.label || value;

  return (
    <div
      style={{
        marginTop: '20px',
        marginRight: '20px',
        width: '200px',
        ...(style || {}),
      }}
      ref={anchorEl}
      className={'default-auto-complete' + (loading ? ' is-loading' : '')}
    >
      {label && (
        <InputLabel shrink id="label">
          {label}
        </InputLabel>
      )}
      <div className="auto-complete-wrapper">
        <Select
          ref={selectRef}
          id={props.id || id || props.name}
          className="auto-complete"
          classes={classes}
          styles={selectStyles}
          clearable={true}
          options={finalOptions}
          autoFocus={autoFocus}
          anchorEl={anchorEl}
          isClearable
          components={{
            Control,
            Menu: popOut ? MenuWithPopper : Menu,
            NoOptionsMessage,
            Option,
            Placeholder,
            SingleValue,
            ValueContainer,
            ...(props.components || {}),
          }}
          maxMenuHeight={maxMenuHeight}
          value={value}
          inputValue={value}
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
          onMenuOpen={handleMenuOpen}
          onMenuClose={handleMenuClose}
          onUpdateInput={(event) => {
            let value = event.target.value;
            if (props.onUpdateInput) props.onUpdateInput(value);
            else if (input) input.onChange(value);
            else onUpdateInput(value);
          }}
          onChange={(selection) => {
            let finalValue = selection;
            if (selection && selection.value && selection.value === selection.label) {
              finalValue = selection.value;
            }
            if (onOptionSelected && selection !== null) {
              onOptionSelected(finalValue);
            }
            if (input) input.onChange(finalValue);
            else if (onChange) onChange(finalValue);
          }}
        />
        {(!!allowClear || !!input.allowClear) && (
          <Icon
            src="circle-xmark"
            fontAwesomeStyle="fas"
            className="clear-icon"
            alt="Clear"
            showTooltip={true}
            onClick={() => {
              if (input) input.onChange('');
              else onChange('');
            }}
          />
        )}
      </div>
    </div>
  );
}

const DefaultAutoCompleteStyled = withStyles(styles, { withTheme: true })(DefaultAutoComplete);
export default DefaultAutoCompleteStyled;
